import { Text, Tooltip } from "@mantine/core";
import styled, { css } from "styled-components";
import TagBadges from "../common/TagBadges";

/**
 * İki rapor tablosunun ortak hücre parçaları.
 *
 * Rapor uçları departman ve etiketleri virgülle birleştirilmiş **düz metin**
 * olarak döndürüyor (`reportService.labelsOf`), boşsa `"-"`. Panelin geri
 * kalanı bunları rozet olarak gösterdiği için burada tekrar parçalanıyor.
 */
export const splitLabels = (value?: string | null): { label: string }[] => {
  if (!value || value.trim() === "" || value.trim() === "-") return [];
  return value
    .split(",")
    .map((part) => ({ label: part.trim() }))
    .filter((tag) => tag.label.length > 0);
};

/**
 * Rozet hücresi.
 *
 * `refs` VERİLİRSE O KULLANILIR: rapor uçları artık etiketin kimlikli hâlini de
 * döndürüyor (`tagRefs`), yani tür ve renk doğrudan kayıttan geliyor.
 *
 * `lookup` yolu (ad → kayıt) geriye dönük duruyor ama BELİRSİZ: aynı ada sahip
 * bir rol ile bir yetenek varsa hangisine denk geldiği rastlantısaldı ve rozet
 * yanlış renk alabiliyordu. Departman gibi kimliksiz kolonlar hâlâ onu kullanıyor.
 */
export const LabelCell = ({
  value,
  refs,
  kind,
  lookup,
  max = 2,
}: {
  value?: string | null;
  refs?: { id: string; name: string; kind?: string; color?: string }[];
  kind: "department" | "role";
  lookup?: Map<string, any>;
  max?: number;
}) => (
  <TagBadges
    max={max}
    tags={
      refs?.length
        ? refs.map((t) => ({
            value: t.id,
            label: t.name,
            kind: t.kind ?? kind,
            color: t.color,
          }))
        : splitLabels(value).map((tag) => {
            const known = lookup?.get(tag.label);
            return {
              ...tag,
              ...(known ?? {}),
              label: tag.label,
              kind: known?.kind ?? kind,
            };
          })
    }
  />
);

export const EmployeeCell = ({ name }: { name?: string }) => (
  <NameWrap>
    <SimpleAvatar>{name ? name[0] : "?"}</SimpleAvatar>
    <Text fw={400} size="md" style={{ color: "#111827" }}>
      {name || "-"}
    </Text>
  </NameWrap>
);

export const PlainCell = ({ children }: { children: any }) => (
  <Text fw={400} size="md" style={{ color: "#111827" }}>
    {children}
  </Text>
);

/**
 * Sayım hücresi: sıfırsa soluk bir "0", sıfırdan büyükse renkli hap.
 *
 * Yalnızca istisna sayan kolonlarda (geç kalma, erken çıkma, eksik çıkış)
 * kullanılıyor; toplam saat / gelinen gün gibi hacim kolonları düz metin
 * kalıyor, aksi halde tablo baştan aşağı renkli hap oluyordu.
 */
export const CountPill = ({
  value,
  tone = "danger",
  title,
}: {
  value?: number;
  tone?: "danger" | "warn" | "ok";
  title?: string;
}) => {
  if (!value || value <= 0) {
    return (
      <Text fw={400} size="md" c="dimmed">
        0
      </Text>
    );
  }

  const pill = <Pill $tone={tone}>{value}</Pill>;
  return title ? (
    <Tooltip label={title} withArrow multiline w={260}>
      {pill}
    </Tooltip>
  ) : (
    pill
  );
};

export const TerminalChip = ({ code }: { code?: string }) =>
  code ? (
    <CodeChip>{code}</CodeChip>
  ) : (
    <Text fw={400} size="md" c="dimmed">
      —
    </Text>
  );

const NameWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

// Çalışanlar tablosundaki avatarla birebir aynı — raporlar Mantine `Badge`
// kullandığı için farklı boyut ve renkte görünüyordu.
const SimpleAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #e1e7fc;
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 14px;
  border: 1px solid #d1d9f5;
  flex-shrink: 0;
`;

const Pill = styled.div<{ $tone: "danger" | "warn" | "ok" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  cursor: default;

  ${(p) => {
    // `ok` (yeşil) 2.49-B ile geldi: DÜZELTİLMİŞ gün sayısı bir eksiklik
    // değil, kapatılmış bir iş. Sarı gösterilseydi "eksik çıkış" kolonuyla
    // aynı okunur ve ayrı sayaç tutmanın anlamı kaçardı.
    if (p.$tone === "ok") {
      return css`
        color: #047857;
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
      `;
    }
    if (p.$tone === "warn") {
      return css`
        color: #b45309;
        background: #fffbeb;
        border: 1px solid #fde68a;
      `;
    }
    return css`
      color: #dc2626;
      background: #fef2f2;
      border: 1px solid #fecaca;
    `;
  }}
`;

const CodeChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.3px;
`;
